import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Mail, RefreshCw, CheckCircle, XCircle, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  sent: "bg-green-100 text-green-800 border-green-300",
  failed: "bg-red-100 text-red-800 border-red-300",
  skipped: "bg-gray-100 text-gray-700 border-gray-300",
};

export const EmailQueuePanel = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}&limit=50` : "?limit=50";
      const res = await axios.get(`${API}/email-queue${params}`);
      setRows(res.data.emails || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error("Email queue laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const markStatus = async (id, status) => {
    setBusy(id);
    try {
      await axios.patch(`${API}/email-queue/${id}`, { status });
      toast.success(`Gemarkeerd als ${status}`);
      fetchQueue();
    } catch {
      toast.error("Update mislukt");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="email-queue-panel">
      <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
        <Mail className="w-5 h-5 text-indigo-500" />
        Email Queue
        <button
          onClick={fetchQueue}
          className="ml-auto text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1"
          data-testid="email-queue-refresh-btn"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> verversen
        </button>
      </h2>

      <div className="bg-white border-2 border-gray-200 p-4 space-y-3">
        {/* Filter pills */}
        <div className="flex flex-wrap gap-1 text-xs">
          {["pending", "sent", "failed", "skipped"].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`email-queue-filter-${s}`}
              className={`px-2 py-1 border ${filter === s ? STATUS_COLORS[s] : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
            >
              {s} ({counts[s] ?? 0})
            </button>
          ))}
        </div>

        {/* Rows */}
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-6 text-xs text-gray-500 flex flex-col items-center gap-1">
            <Inbox className="w-5 h-5 text-gray-300" />
            Geen {filter} berichten
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {rows.map(row => (
              <div key={row.id} className="border border-gray-200 p-2 text-xs hover:border-gray-400 transition-colors" data-testid={`email-queue-row-${row.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={`px-1.5 py-0.5 border ${STATUS_COLORS[row.status] || ''}`}>{row.status}</span>
                  <span className="text-gray-400 font-mono">{row.type}</span>
                </div>
                <div className="font-semibold text-gray-800 truncate">{row.subject}</div>
                <div className="text-gray-500 truncate">{row.to || <em>internal — geen ontvanger</em>}</div>
                {row.status === "pending" && (
                  <div className="flex gap-1 mt-2">
                    <button
                      onClick={() => markStatus(row.id, "sent")}
                      disabled={busy === row.id}
                      data-testid={`email-queue-mark-sent-${row.id}`}
                      className="flex-1 px-2 py-1 border border-green-300 text-green-700 hover:bg-green-50 flex items-center justify-center gap-1"
                    >
                      <CheckCircle className="w-3 h-3" /> mark sent
                    </button>
                    <button
                      onClick={() => markStatus(row.id, "skipped")}
                      disabled={busy === row.id}
                      data-testid={`email-queue-mark-skipped-${row.id}`}
                      className="flex-1 px-2 py-1 border border-gray-300 text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-1"
                    >
                      <XCircle className="w-3 h-3" /> skip
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="text-[10px] text-gray-400 italic pt-1 border-t border-gray-100">
          DB-only queue. SMTP-verzending volgt in een latere stap (Resend/SendGrid).
        </p>
      </div>
    </div>
  );
};
